import React, { useEffect, useRef, useMemo } from 'react';
import { renderPdfPage } from './pdfLoader';
import { debounce } from 'lodash';
import { connect } from 'react-redux';
import { fabric } from 'fabric';
import { registerCanvas } from '../../../redux/actions/canvas_editor';
import {
  clampObjectToCanvas,
  restoreAnnotationsScaled,
  restoreBackendAnnotations,
} from './utils/pdfFabricUtils';

function PdfPage({
  pdfDoc,
  pageNumber,
  scale,
  gap,
  getDocumentBaseScale,
  fabricRegistryRef,
  initialAnnotationsByPage,
  onPageBaseDimensions,
  disabled,
}) {
  const containerRef = useRef(null);

  const pdfCanvasRef = useRef(null); // pdf.js rendered canvas
  const fabricDomRef = useRef(null); // <canvas> DOM element for Fabric
  const fabricCanvasRef = useRef(null); // fabric.Canvas instance

  const pageRef = useRef(null); // pdf.js Page instance
  const baseCanvasWidthRef = useRef(null);
  const baseScaleRef = useRef(1); // initial scale to fit container
  const renderedScaleRef = useRef(1); // last rendered PDF scale
  const readyRef = useRef(false); // indicates that page is fully initialized

  /* ---------- INIT ---------- */
  useEffect(() => {
    let cancelled = false;

    // Load pdf.js page
    pdfDoc.getPage(pageNumber).then((page) => {
      if (cancelled) return;
      pageRef.current = page;
      initPage(page);
    });

    // Cleanup on unmount or page change
    return () => {
      cancelled = true;
      readyRef.current = false;

      // NOTE:
      // We intentionally do NOT destroy Fabric or PDF canvases here,
      // because they are stored in fabricRegistryRef and may be reused
      // when pages are virtualized / re-mounted.
    };
  }, [pdfDoc, pageNumber, getDocumentBaseScale, disabled]);

  function initPage(page) {
    const container = containerRef.current;
    const fabricDom = fabricDomRef.current;

    if (!container || !fabricDom) return;

    // Previously registered Fabric canvas for this page (if any)
    const existingCanvas = fabricRegistryRef.current?.[pageNumber] ?? null;

    // Base viewport at scale = 1
    const viewport = page.getViewport({ scale: 1 });

    // Fit PDF page into container width with padding
    const baseScale = getDocumentBaseScale();
    const fitScale =
      baseScale > 0
        ? baseScale
        : (container.clientWidth / viewport.width) * 0.9;
    baseScaleRef.current = fitScale;

    // Render PDF page at base scale
    renderPdfPage(page, fitScale).then(({ canvas, viewport }) => {
      const newWidth = viewport.width;
      const newHeight = viewport.height;

      /* ---------- PDF CANVAS ---------- */
      canvas.style.width = `${newWidth}px`;
      canvas.style.height = `${newHeight}px`;
      canvas.style.pointerEvents = 'none';

      // Insert PDF canvas below Fabric canvas
      container.insertBefore(canvas, fabricDom);
      pdfCanvasRef.current = canvas;

      /* ---------- FABRIC INIT ---------- */
      const fabricCanvas = new fabric.Canvas(fabricDom, {
        width: newWidth,
        height: newHeight,
        selection: false,
        willReadFrequently: true,
        // perPixelTargetFind: true,
        targetFindTolerance: 5,
        // preserveObjectStacking: true,
      });
      fabricCanvas.skipTargetFind = disabled;

      if (!baseCanvasWidthRef.current) {
        baseCanvasWidthRef.current = newWidth;
      }
      fabricCanvas.pdfScaleFactor =
        newWidth / (baseCanvasWidthRef.current || newWidth);

      fabricCanvas.wrapperEl.style.position = 'absolute';
      fabricCanvas.wrapperEl.style.top = '0';
      fabricCanvas.wrapperEl.style.left = '0';
      fabricCanvas.wrapperEl.style.zIndex = 10;

      if (!disabled) {
        fabricCanvas.on('object:moving', (e) =>
          clampObjectToCanvas(e.target, fabricCanvas)
        );
        fabricCanvas.on('object:scaling', (e) =>
          clampObjectToCanvas(e.target, fabricCanvas)
        );
      }

      // Read-only lightbox canvases stay local so the plan canvas is untouched.
      if (!disabled) {
        registerCanvas(pageNumber, fabricCanvas);
      }
      fabricRegistryRef.current[pageNumber] = fabricCanvas;

      fabricCanvasRef.current = fabricCanvas;

      /* ---------- RESTORE EXISTING OBJECTS ---------- */
      // If this page already had annotations, re-apply them
      // with correct scaling for new canvas size
      if (!!existingCanvas) {
        restoreAnnotationsScaled({
          sourceCanvas: existingCanvas,
          targetCanvas: fabricCanvas,
          oldWidth: existingCanvas.getWidth(),
          oldHeight: existingCanvas.getHeight(),
          newWidth,
          newHeight,
          readOnly: disabled,
        });

        if (existingCanvas !== fabricCanvas) {
          existingCanvas.dispose?.();
        }
      } else {
        restoreBackendAnnotations(
          fabricCanvas,
          initialAnnotationsByPage,
          disabled,
        );
      }

      fabricCanvas.calcOffset();
      fabricCanvas.requestRenderAll();

      renderedScaleRef.current = fitScale;
      readyRef.current = true;

      onPageBaseDimensions &&
        onPageBaseDimensions(pageNumber, {
          width: newWidth,
          height: newHeight,
          fitScale,
        });

      // Apply CSS-only zoom after initial render
      applyCssScale(scale);
    });
  }

  /* ---------- SCALE (PDF + FABRIC WRAPPER) ---------- */
  function applyCssScale(scaleValue) {
    const pdf = pdfCanvasRef.current;
    const fabric = fabricCanvasRef.current;
    const container = containerRef.current; // react-window row container

    if (!pdf || !fabric || !container) return;

    // Calculate CSS scale relative to rendered PDF scale
    const ratio =
      scaleValue / (renderedScaleRef.current / baseScaleRef.current);

    pdf.style.transformOrigin = 'top left';
    pdf.style.transform = `scale(${ratio})`;

    fabric.wrapperEl.style.transformOrigin = 'top left';
    fabric.wrapperEl.style.transform = `scale(${ratio})`;

    // Center page horizontally if it is smaller than container
    const scaledWidth = pdf.offsetWidth * ratio;
    const containerWidth = container.clientWidth;
    const offsetLeft =
      scaledWidth < containerWidth ? (containerWidth - scaledWidth) / 2 : 0;

    pdf.style.marginLeft = `${offsetLeft}px`;
    fabric.wrapperEl.style.marginLeft = `${offsetLeft}px`;

    // Scroll sync logic was intentionally moved out
    // to usePdfScrollSync hook
  }

  /* ---------- DEBOUNCED RE-RENDER ---------- */
  // Heavy PDF re-render is debounced to avoid excessive pdf.js work
  const debouncedRender = useMemo(
    () =>
      debounce((newScale) => {
        const page = pageRef.current;
        const pdf = pdfCanvasRef.current;
        const fabricCanvas = fabricCanvasRef.current;

        if (!page || !pdf || !fabricCanvas) return;

        const finalScale = baseScaleRef.current * newScale;

        const oldWidth = fabricCanvas.getWidth();
        const oldHeight = fabricCanvas.getHeight();

        renderPdfPage(page, finalScale).then(({ canvas, viewport }) => {
          const newWidth = viewport.width;
          const newHeight = viewport.height;

          canvas.style.width = `${newWidth}px`;
          canvas.style.height = `${newHeight}px`;
          canvas.style.pointerEvents = 'none';

          // Replace old PDF canvas
          pdf.replaceWith(canvas);
          pdfCanvasRef.current = canvas;
          renderedScaleRef.current = finalScale;

          // Resize Fabric canvas
          fabricCanvas.setWidth(newWidth);
          fabricCanvas.setHeight(newHeight);
          fabricCanvas.pdfScaleFactor =
            newWidth / (baseCanvasWidthRef.current || newWidth);
          fabricCanvas.calcOffset();

          // Re-scale existing annotations
          restoreAnnotationsScaled({
            sourceCanvas: fabricCanvas,
            targetCanvas: fabricCanvas,
            oldWidth,
            oldHeight,
            newWidth,
            newHeight,
            readOnly: disabled,
          });

          fabricCanvas.requestRenderAll();

          // Apply CSS zoom after re-render
          applyCssScale(newScale);
        });
      }, 800),
    [disabled]
  );

  /* ---------- WATCH SCALE ---------- */
  useEffect(() => {
    if (!readyRef.current) return;
    applyCssScale(scale);
    debouncedRender(scale);
  }, [scale]);

  useEffect(() => {
    return () => {
      debouncedRender.cancel();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        marginTop: gap,
      }}
    >
      {/* Fabric DOM canvas (PDF canvas is injected above it) */}
      <canvas ref={fabricDomRef} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  scale: state.general.lightbox.pdfScale,
});

export default connect(mapStateToProps)(PdfPage);
